// 채팅 관련 API 클라이언트
// OpenAPI로 생성된 클라이언트를 우선 사용하고, 필요한 곳은 래핑합니다.
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { getUserThreads as oasGetUserThreads, createChatThread as oasCreateChatThread, searchChatUsers as oasSearchChatUsers, getPendingChatRequests as oasGetPendingChatRequests, acceptChatRequest as oasAcceptChatRequest, declineChatRequest as oasDeclineChatRequest } from '@edusense/chat-client';
import { createAuthAxiosInstance, tokenManager } from '@edusense/shared-auth-core';
import axios from 'axios';
const trimTrailingSlash = (value)=>value.replace(/\/+$/, '');
const resolveChatBaseUrl = ()=>{
    const viteEnv = typeof import.meta !== 'undefined' && import.meta.env || undefined;
    const candidates = [
        viteEnv?.VITE_CHAT_API_BASE_URL,
        viteEnv?.VITE_API_BASE_URL,
        typeof process !== 'undefined' ? process.env?.VITE_CHAT_API_BASE_URL : undefined,
        typeof process !== 'undefined' ? process.env?.VITE_API_BASE_URL : undefined,
        typeof process !== 'undefined' ? process.env?.NEXT_PUBLIC_API_BASE_URL : undefined
    ];
    const selected = candidates.find((item)=>typeof item === 'string' && item.length > 0);
    if (selected) {
        return trimTrailingSlash(selected);
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
        return trimTrailingSlash(window.location.origin);
    }
    return 'https://api.ugot.uk';
};
const isDevelopmentEnv = ()=>{
    try {
        if (typeof import.meta !== 'undefined' && import.meta.env) {
            return Boolean(import.meta.env.DEV);
        }
    } catch  {
    // ignore runtime detection failures
    }
    if (typeof process !== 'undefined') {
        return process.env?.NODE_ENV === 'development';
    }
    return false;
};
const normalizeUrlPath = (input)=>{
    if (!input) {
        return input;
    }
    if (/^https?:\/\//i.test(input)) {
        return input;
    }
    return input.startsWith('/') ? input : `/${input}`;
};
const ensurePathParam = (value, fallback)=>{
    const segment = String(value ?? '').trim();
    if (segment.length > 0) {
        return segment;
    }
    if (typeof fallback === 'string' && fallback.length > 0) {
        return fallback;
    }
    throw new Error('[ChatApiClient] Path parameter is missing or empty.');
};
let ChatApiClient = class ChatApiClient {
    setToken(token) {
        this.token = token;
        if (typeof window !== 'undefined') {
            try {
                tokenManager.setToken(token);
            } catch (error) {
                console.warn('[ChatApiClient] Failed to sync token with tokenManager:', error);
            }
        }
    }
    resolveActiveToken() {
        if (this.token) {
            return this.token;
        }
        try {
            const managerToken = tokenManager.getToken();
            if (managerToken) {
                this.token = managerToken;
            }
            return managerToken ?? null;
        } catch (error) {
            console.warn('[ChatApiClient] Failed to resolve token from tokenManager:', error);
            return this.token;
        }
    }
    async request(config) {
        const normalizedUrl = normalizeUrlPath(config.url);
        const finalConfig = {
            ...config,
            url: normalizedUrl,
            baseURL: config.baseURL ?? (this.baseURL || undefined)
        };
        // ensure Authorization header exists when token is preloaded (SSR/non-window env)
        if (this.resolveActiveToken() && !finalConfig.headers) {
            finalConfig.headers = {};
        }
        try {
            const response = await this.httpClient.request(finalConfig);
            return {
                data: response.data,
                status: response.status
            };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 401) {
                    console.warn('[ChatApiClient] Unauthorized request detected.', {
                        url: finalConfig.url,
                        method: finalConfig.method
                    });
                }
            }
            throw error;
        }
    }
    // GET 요청
    async get(endpoint, params) {
        return this.request({
            url: endpoint,
            method: 'GET',
            params
        });
    }
    // POST 요청
    async post(endpoint, data) {
        const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
        return this.request({
            url: endpoint,
            method: 'POST',
            data,
            headers: isFormData || data === undefined ? undefined : {
                'Content-Type': 'application/json'
            }
        });
    }
    // PUT 요청
    async put(endpoint, data) {
        const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
        return this.request({
            url: endpoint,
            method: 'PUT',
            data,
            headers: isFormData || data === undefined ? undefined : {
                'Content-Type': 'application/json'
            }
        });
    }
    // DELETE 요청
    async delete(endpoint) {
        return this.request({
            url: endpoint,
            method: 'DELETE'
        });
    }
    // 채팅 스레드 목록 조회
    async getChatThreads(page = 0, size = 5, searchTerm) {
        // OpenAPI 클라이언트 호출(권장 경로)
        // createAuthAxiosInstance가 자동으로 토큰을 추가하므로 수동으로 헤더를 넣지 않음
        const queryParams = {
            page,
            size,
            ...searchTerm ? {
                search: searchTerm
            } : {}
        };
        try {
            const response = await oasGetUserThreads(queryParams);
            // customInstance는 unwrapped 응답을 반환하므로 response가 이미 API 응답
            const arr = response;
            // 배열 형태(Flow 직렬화) → 페이지 어댑트
            if (Array.isArray(arr)) {
                const adapted = {
                    content: arr,
                    number: page,
                    size,
                    totalElements: arr.length,
                    totalPages: 1,
                    last: true,
                    first: page === 0
                };
                return {
                    data: adapted,
                    status: 200
                };
            }
            // 페이징 형태면 그대로
            if (arr && typeof arr === 'object' && 'content' in arr) {
                return {
                    data: arr,
                    status: 200
                };
            }
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                console.warn('[ChatApiClient] Unauthorized when fetching chat threads. Returning empty dataset.');
                return {
                    data: {
                        content: [],
                        number: page,
                        size,
                        totalElements: 0,
                        totalPages: 0,
                        last: true,
                        first: page === 0
                    },
                    status: 401,
                    message: 'Unauthorized'
                };
            }
            throw error;
        }
        // 폴백: 빈 페이지
        return {
            data: {
                content: [],
                number: page,
                size,
                totalElements: 0,
                totalPages: 0,
                last: true,
                first: page === 0
            },
            status: 200
        };
    }
    async getPendingChatRequests() {
        // createAuthAxiosInstance가 자동으로 토큰을 추가하므로 수동으로 헤더를 넣지 않음
        const res = await oasGetPendingChatRequests();
        // customInstance는 unwrapped 응답을 반환하므로 res가 이미 API 응답
        return {
            data: res,
            status: 200
        };
    }
    async acceptChatRequest(chatRoomId) {
        // createAuthAxiosInstance가 자동으로 토큰을 추가하므로 수동으로 헤더를 넣지 않음
        const res = await oasAcceptChatRequest(chatRoomId);
        // customInstance는 unwrapped 응답을 반환
        return {
            data: res,
            status: 200
        };
    }
    async declineChatRequest(chatRoomId) {
        // createAuthAxiosInstance가 자동으로 토큰을 추가하므로 수동으로 헤더를 넣지 않음
        const res = await oasDeclineChatRequest(chatRoomId);
        // customInstance는 unwrapped 응답을 반환
        return {
            data: res,
            status: 200
        };
    }
    // 채팅 스레드 삭제
    async deleteChatThread(threadId) {
        const roomPath = ensurePathParam(threadId);
        return this.delete(`/api/chat/chat-threads/${roomPath}`);
    }
    // 메시지 읽음 처리
    async markMessageAsRead(roomId, messageId) {
        const roomPath = ensurePathParam(roomId);
        const messagePath = ensurePathParam(messageId, '0');
        return this.post(`/api/chat/chat-threads/${roomPath}/messages/${messagePath}/read`);
    }
    // 채팅 스레드의 마지막 메시지 업데이트
    async updateChatThreadLastMessage(roomId, data) {
        const roomPath = ensurePathParam(roomId);
        return this.put(`/api/chat/chat-threads/${roomPath}/last-message`, data);
    }
    // 새 채팅 스레드 생성
    async createChatThread(data) {
        const payload = {
            participantIds: (data.participantUserIds || []).map((value)=>String(value).trim()).filter((value)=>value.length > 0),
            isGroupThread: data.isGroupThread,
            initialMessage: data.initialMessage
        };
        // createAuthAxiosInstance가 자동으로 토큰을 추가하므로 수동으로 헤더를 넣지 않음
        const res = await oasCreateChatThread(payload);
        // customInstance는 unwrapped 응답을 반환하므로 res가 이미 API 응답
        return {
            data: res,
            status: 200
        };
    }
    // 채팅방 나가기
    async leaveChatRoom(roomId) {
        const roomPath = ensurePathParam(roomId);
        return this.post(`/api/chat/chat-threads/${roomPath}/leave`);
    }
    // 채팅방에 사용자 초대
    async inviteUsersToChatRoom(roomId, userIds) {
        const payload = {
            userIds: (userIds || []).map((value)=>String(value).trim()).filter((value)=>value.length > 0)
        };
        if (payload.userIds.length === 0) {
            return {
                data: undefined,
                status: 204
            };
        }
        const roomPath = ensurePathParam(roomId);
        return this.post(`/api/chat/chat-threads/${roomPath}/invite`, payload);
    }
    // 채팅방에서 사용자 제거
    async removeUserFromChatRoom(roomId, userId) {
        const roomPath = ensurePathParam(roomId);
        const userPath = ensurePathParam(userId);
        return this.delete(`/api/chat/chat-threads/${roomPath}/users/${userPath}`);
    }
    // 특정 채팅방의 메시지 목록 가져오기
    async getMessages(roomId, page = 0, size = 20) {
        const roomPath = ensurePathParam(roomId);
        return this.get(`/api/chat/chat-threads/${roomPath}/messages`, {
            page,
            size
        });
    }
    // 메시지 전송
    async sendMessage(roomId, data) {
        const roomPath = ensurePathParam(roomId);
        return this.post(`/api/chat/chat-threads/${roomPath}/messages`, data);
    }
    // 메시지 반응 추가
    async addMessageReaction(roomId, messageId, reactionType) {
        const roomPath = ensurePathParam(roomId);
        const messagePath = ensurePathParam(messageId);
        return this.post(`/api/chat/chat-threads/${roomPath}/messages/${messagePath}/reactions`, {
            reactionType
        });
    }
    // 메시지 반응 제거
    async removeMessageReaction(roomId, messageId, reactionType) {
        const roomPath = ensurePathParam(roomId);
        const messagePath = ensurePathParam(messageId);
        return this.delete(`/api/chat/chat-threads/${roomPath}/messages/${messagePath}/reactions/${reactionType}`);
    }
    // 메시지 검색 (특정 채팅방 내)
    async searchMessages(roomId, params) {
        const roomPath = ensurePathParam(roomId);
        return this.get(`/api/chat/chat-threads/${roomPath}/messages/search`, {
            q: params.query,
            page: params.page || 0,
            size: params.size || 20
        });
    }
    // 전체 채팅방에서 메시지 검색
    async searchAllMessages(params) {
        return this.get('/api/chat/chat-threads/messages/search', {
            q: params.query,
            page: params.page || 0,
            size: params.size || 20
        });
    }
    // 사용자 검색 (새 채팅 생성용)
    async searchUsers(searchTerm, page = 0, size = 10) {
        try {
            // createAuthAxiosInstance가 자동으로 토큰을 추가하므로 수동으로 헤더를 넣지 않음
            const options = {
                timeout: 10000
            };
            const res = await oasSearchChatUsers({
                query: searchTerm,
                page,
                size
            }, options);
            // customInstance는 unwrapped 응답을 반환하므로 res가 이미 API 응답
            return {
                data: res,
                status: 200
            };
        } catch (error) {
            // Axios 에러를 감지하고 더 나은 에러 객체로 변환
            if (axios.isAxiosError(error)) {
                const enhancedError = new Error(error.response?.data?.message || error.message || 'User search failed');
                enhancedError.response = error.response;
                enhancedError.code = error.code;
                throw enhancedError;
            }
            throw error;
        }
    }
    // 파일 업로드
    async uploadFile(file) {
        const formData = new FormData();
        formData.append('file', file);
        return this.request({
            url: '/upload/file',
            method: 'POST',
            data: formData
        });
    }
    // 채팅방 설정 업데이트
    async updateChatRoomSettings(roomId, settings) {
        return this.put(`/api/chat/chat-threads/${roomId}/settings`, settings);
    }
    // 채팅방 정보 조회
    async getChatRoomInfo(roomId) {
        return this.get(`/api/chat/chat-threads/${roomId}`);
    }
    // 온라인 사용자 목록 조회
    async getOnlineUsers(roomId) {
        return this.get(`/api/chat/chat-threads/${roomId}/online-users`);
    }
    // 대화 목록 조회 (PeopleChatModal용)
    async getConversations() {
        const response = await this.getChatThreads();
        return response.data?.content || [];
    }
    // 그룹 대화 생성
    async createGroupConversation(userIds, groupName) {
        const payload = {
            participantIds: userIds.map((id)=>String(id).trim()).filter((value)=>value.length > 0),
            isGroupThread: true
        };
        // createAuthAxiosInstance가 자동으로 토큰을 추가하므로 수동으로 헤더를 넣지 않음
        const response = await oasCreateChatThread(payload);
        // customInstance는 unwrapped 응답을 반환
        return response;
    }
    // 1:1 대화 생성
    async createConversation(userId) {
        const payload = {
            participantIds: [
                String(userId).trim()
            ].filter((value)=>value.length > 0),
            isGroupThread: false
        };
        // createAuthAxiosInstance가 자동으로 토큰을 추가하므로 수동으로 헤더를 넣지 않음
        const response = await oasCreateChatThread(payload);
        // customInstance는 unwrapped 응답을 반환
        return response;
    }
    constructor(baseURL = resolveChatBaseUrl()){
        _define_property(this, "baseURL", void 0);
        _define_property(this, "httpClient", void 0);
        _define_property(this, "token", null);
        _define_property(this, "tokenChangeListener", void 0);
        this.baseURL = trimTrailingSlash(baseURL);
        this.httpClient = createAuthAxiosInstance({
            baseURL: this.baseURL || undefined,
            timeout: 30000,
            enableDebug: isDevelopmentEnv()
        });
        if (typeof window !== 'undefined') {
            try {
                const existingToken = tokenManager.getToken();
                if (existingToken) {
                    this.token = existingToken;
                }
            } catch (error) {
                console.warn('[ChatApiClient] Failed to read tokenManager token on init:', error);
            }
            this.tokenChangeListener = (nextToken)=>{
                this.token = nextToken;
            };
            try {
                tokenManager.addTokenChangeListener(this.tokenChangeListener);
            } catch (error) {
                console.warn('[ChatApiClient] Failed to subscribe to tokenManager changes:', error);
            }
        } else {
            this.tokenChangeListener = ()=>{};
        }
    }
};
// 싱글톤 인스턴스
export const chatApiClient = new ChatApiClient();
// realMessageAPI 호환 객체
export const realMessageAPI = {
    sendMessage: async (roomId, message)=>{
        return chatApiClient.sendMessage(roomId, {
            content: message
        });
    },
    createGroupConversation: async (userIds, groupName)=>{
        return chatApiClient.createGroupConversation(userIds, groupName);
    },
    createConversation: async (userId)=>{
        return chatApiClient.createConversation(userId);
    }
};
// 편의 함수들 export
export const getConversations = ()=>chatApiClient.getConversations();
export const getMessages = (roomId)=>chatApiClient.getMessages(roomId);
export const getPendingChatRequests = ()=>chatApiClient.getPendingChatRequests();
export const acceptChatRequest = (roomId)=>chatApiClient.acceptChatRequest(roomId);
export const declineChatRequest = (roomId)=>chatApiClient.declineChatRequest(roomId);
export default chatApiClient;
